import { useUser } from "@clerk/clerk-expo";
import { events } from "@constants/tracking";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { useAppStore } from "@hooks/useAppStore";
import useClerkUser from "@hooks/useClerkUser";
import { useQuery } from "@tanstack/react-query";
import { trackCustomEvent } from "analytics";
import {
	applyAccessCode,
	getActiveAccessCodes,
} from "api/commerce/access-code-service";
import type { ActiveCodesResponse } from "api/commerce/types";
import { useEffect, useMemo, useRef, useState } from "react";

const HOME_PATH = "/(protected)/(tabs)/home";
const AUTO_REDEEM_SOURCE = "post_login_auto_redeem";
const NAVIGATION_DELAY_MS = 150;

type LoadingKey = "session" | "accessCode" | "exams";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getLoadingMessages = (loadingKey: LoadingKey) => {
	const messageMap = {
		session: {
			text: "Securely logging you in",
			icon: "shield-check-outline" as const,
		},
		accessCode: {
			text: "Activating your access",
			icon: "lock-open-outline" as const,
		},
		exams: {
			text: "Loading modules",
			icon: "book-open-outline" as const,
		},
	};

	return [messageMap[loadingKey]];
};

const getErrorMessage = (error: unknown) => {
	if (error instanceof Error) {
		return error.message;
	}

	return String(error);
};

const capturePostLoginError = (
	error: unknown,
	extraData: Record<string, unknown>,
) => {
	captureErrorToSentry(
		error instanceof Error
			? error
			: new Error("Error during post-login access activation"),
		{
			...extraData,
			error: getErrorMessage(error),
		},
	);
};

const getResultExamId = (
	result: Awaited<ReturnType<typeof applyAccessCode>>,
) => {
	return result.data.examId || result.productData?.[0]?.examId || "";
};

const usePostLoginAccess = () => {
	const tenantId = useAppStore((state) => state.tenant.id);
	const { isLoaded, isSignedIn, userLogicalId } = useClerkUser();
	const { user } = useUser();
	const hasStartedRef = useRef(false);
	const [loadingKey, setLoadingKey] = useState<LoadingKey>("session");
	const [nextPath, setNextPath] = useState<string | null>(null);

	useQuery({
		queryKey: ["post-login-user-ready", user?.id],
		queryFn: async () => {
			const updated = await user?.reload();
			return updated ?? null;
		},
		enabled: isSignedIn && !userLogicalId,
		refetchInterval: 500,
	});

	useEffect(() => {
		let cancelled = false;

		const navigateHome = async () => {
			setLoadingKey("exams");
			await wait(NAVIGATION_DELAY_MS);
			if (!cancelled) {
				setNextPath(HOME_PATH);
			}
		};

		const activateAccess = async () => {
			if (!(isLoaded && isSignedIn && tenantId && userLogicalId)) {
				setLoadingKey("session");
				return;
			}

			if (hasStartedRef.current) {
				return;
			}
			hasStartedRef.current = true;

			setLoadingKey("accessCode");

			let activeAccessCodes: ActiveCodesResponse[] = [];
			try {
				activeAccessCodes = await getActiveAccessCodes();
			} catch (error) {
				capturePostLoginError(error, {
					step: "fetch_active_access_codes",
					tenantId,
				});
				await navigateHome();
				return;
			}

			if (cancelled) {
				return;
			}

			if (!activeAccessCodes?.length) {
				await navigateHome();
				return;
			}

			for (const activeAccessCode of activeAccessCodes) {
				const accessCode = activeAccessCode.codes?.[0];
				if (!accessCode) {
					continue;
				}

				try {
					trackCustomEvent({
						eventName: events.promoCodeEntered,
						code: accessCode,
						source: AUTO_REDEEM_SOURCE,
					});

					const result = await applyAccessCode({ accessCode });
					if (!result.success) {
						capturePostLoginError(
							new Error(result.message || "Access code redemption failed"),
							{
								step: "redeem_active_access_code",
								accessCode,
								tenantId,
							},
						);
						continue;
					}

					trackCustomEvent({
						eventName: events.promoCodeRedeemed,
						code: accessCode,
						source: AUTO_REDEEM_SOURCE,
						examId: getResultExamId(result),
						examNumericId: result.data.examNumericId,
					});
				} catch (error) {
					capturePostLoginError(error, {
						step: "redeem_active_access_code",
						accessCode,
						tenantId,
					});
				}
			}

			if (cancelled) {
				return;
			}

			await navigateHome();
		};

		activateAccess().catch((error) => {
			capturePostLoginError(error, {
				step: "activate_access",
				tenantId,
			});
			navigateHome();
		});

		return () => {
			cancelled = true;
		};
	}, [isLoaded, isSignedIn, tenantId, userLogicalId]);

	const messages = useMemo(() => getLoadingMessages(loadingKey), [loadingKey]);

	return {
		messages,
		nextPath,
	};
};

export default usePostLoginAccess;
